import logger from 'loglevel';
import { ConsentsCategory } from './consents';
import { canSubscribeToAnalyticsConsent } from './consents-manager-tcf-api';
/**
 * Fallback if TCF fails to initialize
 * These should always be "unknown" since we don't know the user's choice
 * @returns
 */
function getFallbackConsents() {
    return {
        purposes: {
            [ConsentsCategory.CMP_ADVERTISING]: {
                optIn: false,
                status: 'unknown',
            },
            [ConsentsCategory.CMP_ANALYTICS]: {
                optIn: false,
                status: 'unknown',
            },
            [ConsentsCategory.CMP_MARKETING]: {
                optIn: false,
                status: 'unknown',
            },
            [ConsentsCategory.CMP_PERSONALIZATION]: {
                optIn: false,
                status: 'unknown',
            },
        },
        source: 'default',
    };
}
/**
 * Returns the consent manager (`_tcf_`), if available.
 * It will await "cmp:loaded" event if the consent manager is not available yet and return it when it is.
 * @returns The consent manager object.
 * @example
 * const manager = await consentManager();
 */
export const consentManager = () => {
    // For ConsentManager/window._tcf_ type, either duck type something yourself for now or wait for better types to be exposed from sourcepoint package.
    const getManager = () => window._tcf_;
    return new Promise((resolve, reject) => {
        const manager = getManager();
        if (manager) {
            resolve(manager);
        }
        else {
            let hasTimedOut = false;
            const timeoutForManager = setTimeout(() => {
                logger.error('Pulse - timed out waiting for consentManager');
                hasTimedOut = true;
                reject(new Error('Pulse - timed out waiting for consentManager'));
            }, 5000);
            document.addEventListener('cmp:loaded', () => {
                if (hasTimedOut) {
                    logger.error('Pulse - consentManager triggered after timeout');
                    return;
                }
                clearTimeout(timeoutForManager);
                resolve(getManager());
            }, {
                once: true,
            });
        }
    });
};
/**
 * Waits for user to reply to consents, then retrieves the cached or default consents for Pulse from the consent manager.
 * Only needed if you want to wait for user to respond to consents rather than sending events with default consent
 * WARNING: Do not use this without analytics consent being activated in TCF or it will never resolve for users without cached consents!
 * @returns A promise with the cached or default consents for Pulse.
 * @example
 * const getConsentsWaitForAnalyticsPromise = getConsentsWaitForAnalytics();
 */
export const getConsentsWaitForAnalytics = async (callback) => {
    const consents = await getInitialConsentsFromTCF();
    // no need to subscribe if we don't receive a callback to call
    if (callback) {
        await subscribeToConsentUpdates(callback);
    }
    return consents;
};
const subscribeToConsentUpdates = async (callback) => {
    let manager;
    try {
        manager = await consentManager();
    }
    catch (error) {
        logger.error('Unable to subscribe as tcf manager is not initialized', error);
        // If we can't get the consent manager, we can't subscribe to updates
        return;
    }
    manager.subscribeAny(() => {
        const consents = manager.getCachedOrDefaultConsentsForPulse();
        logger.debug('CMP:subscribeToConsentUpdates:subscribeAny - resolved after user replied to consent dialog', consents);
        callback(consents);
    });
};
const getInitialConsentsFromTCF = async () => {
    let manager;
    try {
        manager = await consentManager();
    }
    catch (error) {
        logger.error('Failed to get consent manager, using pulse default consents', error);
        return getFallbackConsents();
    }
    // Check if we need to wait for analytics consent. (Only needed until end of January 2025)
    const shouldWait = await canSubscribeToAnalyticsConsent();
    if (shouldWait) {
        // Check if analytics consent exists in cache.
        const analyticsConsent = manager.getPermissionSync('CMP:analytics');
        // null analyticsConsent means that the user has not yet replied to the consent dialog.
        if (analyticsConsent === null) {
            // Wait for the user to reply to the consent dialog.
            return new Promise((resolve) => {
                // Need to use subscribeAny since that is the only one resolving after all consents are properly set initially
                const unsubscribe = manager.subscribeAny(() => {
                    logger.debug('CMP:getConsentsWaitForAnalytics - resolved after user replied to consent dialog');
                    resolve(manager.getCachedOrDefaultConsentsForPulse());
                    unsubscribe();
                });
                return;
            });
        }
    }
    // If we have consent in cache or do not need to wait, return the consents immediately.
    logger.debug('CMP:getConsentsWaitForAnalytics - resolved immediately');
    return manager.getCachedOrDefaultConsentsForPulse();
};
